import PaymentContext from 'context/PaymentContext';
import { useEffectOnce } from 'hooks/useEffectOnce';
import React, { useCallback, useContext, useState, useId } from 'react';
import { Button, Spinner, Table } from 'react-bootstrap';
import { message, Tooltip } from 'antd';
import ViewListData from './ViewListData';
import './meetinghistory.css';
import FilterModal from 'components/Modal/FilterModal';
import { filter } from 'assets/images';
import DemoContext from 'context/DemoContext';
import { DATE_FORMATS, GENERIC_ERROR_MESSAGE, SORTING_STATUS } from 'utils/constants';
import { TEST_IDS } from './testIds';
import { CaretDown, CaretUp } from 'react-bootstrap-icons';
import InfiniteScroll from 'react-infinite-scroll-component';
/**
 * This component renders all the demos history related information to the store admin.
 *
 * @return {ReactNode} A React element that renders all the demos history related information to the store admin.
 */

const MeetingStoreView = ({question_id}) => {
  const {
    storePayoutGet,
    storePayoutGetWithPagination,
    demoHistoryStore,
    demoHistoryStoreWithPagination,
    setDemoHistoryStoreWithPagination,
    nextStorePayout,
    previousStorePayout,
  } = useContext(PaymentContext);

  const { fetchAllHosts, fetchAllProducts } = useContext(DemoContext);
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [filteredDataLoading, setFilteredDataLoading] = useState(false);
  const [clearFiltersLoading, setClearFiltersLoading] = useState(false);
  const [loadingPros, setLoadingPros] = useState(false);
  const [filterShow, setFilterShow] = useState(false);

  const [dateFilter, setDateFilter] = useState('');
  const [originalDateFilter, setOriginalDateFilter] = useState('');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [originalStartDateFilter, setOriginalStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const [originalEndDateFilter, setOriginalEndDateFilter] = useState('');
  const [hostFilter, setHostFilter] = useState('');
  const [originalHostFilter, setOriginalHostFilter] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [originalProductFilter, setOriginalProductFilter] = useState('');
  const [sorting, setSorting] = useState({
    date: SORTING_STATUS.NONE,
    productName: SORTING_STATUS.NONE,
    result: SORTING_STATUS.NONE,
    duration: SORTING_STATUS.NONE,
    guestName: SORTING_STATUS.NONE,
    hostName: SORTING_STATUS.NONE,
  });

  // Generate unique ID for the scrollable div
  const scrollableDivId = useId().replace(/:/g, '') + '-scrollable';

    /**
   * This function is used to load more data when scrolled to the bottom of the page
   *
   * It makes the API call to get the next set of data and we fetch it from contenxt.
   */
  const fetchMoreData = useCallback(async () => {
    if (!nextStorePayout || loading) {
      setHasMore(false);
      return;
    }
    try {
      setLoading(true);
      const nextPathname = new URL(nextStorePayout).search;
      await storePayoutGetWithPagination(nextPathname);
      if (nextStorePayout) {
        setHasMore(true);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setHasMore(false);
      messageApi.open({
        type: "error",
        content: error?.message || GENERIC_ERROR_MESSAGE,
        duration: 5,
      });
    }
  }, [nextStorePayout, demoHistoryStoreWithPagination, messageApi]);

  /**
   * This function is used to get the current sorting state
   *
   * @return {array} array of two values: columnName and sorting direction
   */

  const getCurrentSorting = () => {
    const keys = Object.keys(sorting);
    for (let index = 0; index < keys.length; index++) {
      if (
        sorting[keys[index]] === SORTING_STATUS.ASCENDING ||
        sorting[keys[index]] === SORTING_STATUS.DESCENDING
      ) {
        return [keys[index], sorting[keys[index]]];
      }
    }
    return [null, null];
  };

  /**
   * This function is the on close fuction which manages the states on close of the filter modal
   *
   * @param {Boolean} setAllStates boolean flag to reset all the states to its original state
   * @return {void}
   */

  const handleFilterClose = (setAllStates) => {
    if (setAllStates) {
      setDateFilter(originalDateFilter);
      setStartDateFilter(originalStartDateFilter);
      setEndDateFilter(originalEndDateFilter);
      setHostFilter(originalDateFilter);
      setProductFilter(originalProductFilter);
    }
    setFilterShow(false);
  };

  useEffectOnce(() => {
    const res = storePayoutGet(`?order=true&question=${question_id}`);
    res
      .then(() => setLoading(false))
      .catch((err) => {
        setLoading(true);
      });

    fetchAllHosts('?')
      .then(() => {})
      .catch((err) => {
        console.log(err);
      });

    fetchAllProducts('?')
      .then(() => {})
      .catch((err) => {
        console.log(err);
      });

    return () => {
      setDemoHistoryStoreWithPagination([]);
    }
  }, [demoHistoryStoreWithPagination]);

  /**
   * This function is used to handle the api call with all the filters when filters are applied.
   *
   * @return {void}
   */

  const applyFilters = () => {
    setFilteredDataLoading(true);
    let startD = '';
    let endD = '';
    if (startDateFilter) {
      startD = startDateFilter.format(DATE_FORMATS.GENERIC_DATE_FORMAT);
    }
    if (endDateFilter) {
      endD = endDateFilter.format(DATE_FORMATS.GENERIC_DATE_FORMAT);
    }

    const currentSorting = getCurrentSorting();
    let res;

    if (currentSorting[0]) {
      res = storePayoutGet(
        `?order=true&startDate=${startD}&endDate=${endD}&host=${hostFilter}&product=${productFilter}&${currentSorting[0]}=${currentSorting[1]}&question=${question_id}`
      );
    } else {
      res = storePayoutGet(
        `?order=true&startDate=${startD}&endDate=${endD}&host=${hostFilter}&product=${productFilter}&question=${question_id}`
      );
    }

    res
      .then((response) => {
        setFilteredDataLoading(false);
        setOriginalDateFilter(dateFilter);
        setOriginalStartDateFilter(startDateFilter);
        setOriginalEndDateFilter(endDateFilter);
        setOriginalHostFilter(hostFilter);
        setOriginalProductFilter(productFilter);
        setHasMore(response?.data?.data?.next ? true : false);
        handleFilterClose();
      })
      .catch((err) => {
        setFilteredDataLoading(true);
      });
  };

  /**
   * This function is used to handle the api call for clearing all the filters when filters are removed.
   *
   * @return {void}
   */

  const clearFilters = () => {
    setClearFiltersLoading(true);

    let res;
    const currentSorting = getCurrentSorting();

    if (currentSorting[0]) {  
      res = storePayoutGet(`?order=true&question=${question_id}&${currentSorting[0]}=${currentSorting[1]}`);
    } else {
      res = storePayoutGet(`?order=true&question=${question_id}`);
    }

    res
      .then((response) => {
        setClearFiltersLoading(false);
        setOriginalDateFilter('');
        setOriginalStartDateFilter('');
        setOriginalEndDateFilter('');
        setOriginalHostFilter('');
        setOriginalProductFilter('');
        setDateFilter('');
        setStartDateFilter('');
        setEndDateFilter('');
        setHostFilter('');
        setProductFilter('');
        handleFilterClose();
        setHasMore(true);
      })
      .catch((err) => {
        setClearFiltersLoading(true);
      });
  };

  /**
   * This function is used to get the list of history of demos
   *
   * @return {ReactNode} A component returns a list of line items for each demo
   */

  const getlist = () => {
      if (!loading && demoHistoryStoreWithPagination.length < 1) {
        return (
          <tr>
            <td>No data available!</td>
          </tr>
        );
      } else {
        return demoHistoryStoreWithPagination?.map((dataList, index) => {
          return <ViewListData key={index} dataList={dataList} />;
        });
      }
  };

  /**
   * This function is used to handle sorting on the table columns
   *
   * @return {void}
   */

  const handleSorting = (column) => {
    if (loading) {
      return;
    }
    let startD = '';
    let endD = '';
    let sortingDirection = '';
    if (startDateFilter) {
      startD = startDateFilter.format(DATE_FORMATS.GENERIC_DATE_FORMAT);
    }
    if (endDateFilter) {
      endD = endDateFilter.format(DATE_FORMATS.GENERIC_DATE_FORMAT);
    }

    if (sorting[column] === SORTING_STATUS.ASCENDING) {
      sortingDirection = SORTING_STATUS.DESCENDING;
    } else if (sorting[column] === SORTING_STATUS.DESCENDING) {
      sortingDirection = SORTING_STATUS.NONE;
    } else {
      sortingDirection = SORTING_STATUS.ASCENDING;
    }

    setDemoHistoryStoreWithPagination([]);

    const res = storePayoutGet(
      `?order=true&startDate=${startD}&endDate=${endD}&host=${hostFilter}&product=${productFilter}&${column}=${sortingDirection}&question=${question_id}`
    );
    setLoading(true);
    res
      .then(() => {
        const newSorting = { ...sorting };
        Object.entries(newSorting).forEach(([key, value]) => {
          if (key === column && value === SORTING_STATUS.ASCENDING) {
            newSorting[key] = SORTING_STATUS.DESCENDING;
          } else if (
            key === column &&
            sorting[column] === SORTING_STATUS.DESCENDING
          ) {
            newSorting[key] = SORTING_STATUS.NONE;
          } else if (
            key === column &&
            sorting[column] === SORTING_STATUS.NONE
          ) {
            newSorting[key] = SORTING_STATUS.ASCENDING;
          } else {
            newSorting[key] = SORTING_STATUS.NONE;
          }
        });
        setLoading(false);
        setHasMore(true);
        setSorting(newSorting);
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
      });
  };

  /**
   * This function is used to get the tooltip message
   *
   * @return {string} message to be displayed in the tooltip
   */

  const getTooltipTitle = (columnName) => {
    if (sorting[columnName] === SORTING_STATUS.ASCENDING) {
      return 'Click here to sort descending';
    } else if (sorting[columnName] === SORTING_STATUS.DESCENDING) {
      return 'Click here to clear sorting';
    } else {
      return 'Click here to sort ascending';
    }
  };

  return (
    <>
    {contextHolder}
      <div className="filter-btn">
        <Button
          data-testid={TEST_IDS.FILTERS_BTN}
          onClick={() => setFilterShow(true)}
        >
          <img src={filter} alt="" /> Filters
        </Button>
      </div>
      <div className="table-container" id={scrollableDivId} style={{ height: '600px', overflow: 'auto' }}>
        <InfiniteScroll
          dataLength={demoHistoryStoreWithPagination.length}
          next={fetchMoreData}
          hasMore={hasMore}
          loader={
            loading && <div className="d-flex justify-content-center">
              <Spinner animation="grow" />
              <Spinner animation="grow" />
              <Spinner animation="grow" />
            </div>
          }
          scrollableTarget={scrollableDivId}
          endMessage={
            <div className='text-center py-3 text-muted'>
              {!hasMore && "No more data to load."}
            </div>
          }
        >
      <div className="store-payout-history-wrapper table-responsive">
        <Table className="table-store-payout">
          <thead>
            <tr>
              <th className="date-head">
                <div className="arrows">
                  Date{' '}
                  <Tooltip title={getTooltipTitle('date')}>
                    <div
                      className="arrow-grid"
                      onClick={() => {
                        handleSorting('date');
                      }}
                    >
                      <CaretUp />
                      <CaretDown />
                    </div>
                  </Tooltip>
                </div>
              </th>
              <th className="product-head">
                <div className="arrows">
                  Product{' '}
                  <Tooltip title={getTooltipTitle('productName')}>
                    <div
                      className="arrow-grid"
                      onClick={() => {
                        handleSorting('productName');
                      }}
                    >
                      <CaretUp />
                      <CaretDown />
                    </div>
                  </Tooltip>
                </div>
              </th>
              <th className="result-head">
                <div className="arrows">
                  Result{' '}
                  <Tooltip title={getTooltipTitle('result')}>
                    <div
                      className="arrow-grid"
                      onClick={() => {
                        handleSorting('result');
                      }}
                    >
                      <CaretUp />
                      <CaretDown />
                    </div>
                  </Tooltip>
                </div>
              </th>
              <th className="duration-head">
                <div className="arrows">
                  Duration{' '}
                  <Tooltip title={getTooltipTitle('duration')}>
                    <div
                      className="arrow-grid"
                      onClick={() => {
                        handleSorting('duration');
                      }}
                    >
                      <CaretUp />
                      <CaretDown />
                    </div>
                  </Tooltip>
                </div>
              </th>
              <th className="host-head">
                <div className="arrows">
                  Real Customer{' '}
                  <Tooltip title={getTooltipTitle('hostName')}>
                    <div
                      className="arrow-grid"
                      onClick={() => {
                        handleSorting('hostName');
                      }}
                    >
                      <CaretUp />
                      <CaretDown />
                    </div>
                  </Tooltip>
                </div>
              </th>
              <th className="guest-head">
                <div className="arrows">
                  Guest{' '}
                  <Tooltip title={getTooltipTitle('guestName')}>
                    {/* <div
                      className="arrow-grid"
                      onClick={() => {
                        handleSorting('guestName');
                      }}
                    >
                      <CaretUp />
                      <CaretDown />
                    </div> */}
                  </Tooltip>
                </div>
              </th>
            </tr>
          </thead>
          <tbody id="scrollableDiv">{getlist()}</tbody>
        </Table>
      </div>
      </InfiniteScroll>
      </div>
      
      <FilterModal
        showStatus={filterShow}
        clickStatus={handleFilterClose}
        buttonMessage="Close"
        closeStatus={handleFilterClose}
        secondMessage="none"
        secondButton={false}
        dateFilter={dateFilter}
        setDateFilter={setDateFilter}
        setStartDateFilter={setStartDateFilter}
        setEndDateFilter={setEndDateFilter}
        setHostFilter={setHostFilter}
        setProductFilter={setProductFilter}
        startDateFilter={startDateFilter}
        endDateFilter={endDateFilter}
        hostFilter={hostFilter}
        productFilter={productFilter}
        applyFilters={applyFilters}
        originalDateFilter={originalDateFilter}
        originalStartDateFilter={originalStartDateFilter}
        originalEndDateFilter={originalEndDateFilter}
        originalHostFilter={originalHostFilter}
        originalProductFilter={originalProductFilter}
        clearFilters={clearFilters}
        loading={filteredDataLoading}
        clearFiltersLoading={clearFiltersLoading}
        parentContainer={question_id ? null : document.body}
      />
    </>
  );
};

export default MeetingStoreView;
